import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Smartphone, ArrowRight } from 'lucide-react';

export const AppStores = () => {
  const { language } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();

  const t = translations.appStores;

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 theme-transition overflow-hidden"
      data-testid="app-stores-section"
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className={`${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-6">
              <Smartphone className="w-4 h-4" strokeWidth={1.5} />
              <span>{t.badge[language]}</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              {t.title[language]}
            </h2>
            
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-lg">
              {t.subtitle[language]}
            </p>

            {/* Features List */}
            <ul className="space-y-3 mb-8">
              {t.features[language].map((feature, index) => (
                <li 
                  key={index}
                  className="flex items-center gap-3 text-slate-700 dark:text-slate-300"
                >
                  <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <a 
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-semibold hover:gap-3 transition-all"
              data-testid="mobile-cta"
            >
              {t.cta[language]}
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>

          {/* Right - Visual */}
          <div 
            className={`relative ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.15s' }}
          >
            <div className="relative flex justify-center items-center">
              {/* Background Glow */}
              <div className="absolute w-80 h-80 bg-indigo-500/20 dark:bg-indigo-500/10 rounded-full blur-3xl" />
              
              {/* Phone Mockups Container */}
              <div className="relative flex items-end justify-center gap-4">
                {/* Android Phone */}
                <div className="relative z-10 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="w-44 h-80 bg-slate-900 rounded-[2rem] p-2 shadow-2xl shadow-slate-900/50">
                    <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-violet-600 rounded-[1.5rem] relative overflow-hidden">
                      {/* Status Bar */}
                      <div className="flex justify-between items-center px-4 pt-3 text-white/80 text-[10px]">
                        <span>9:41</span>
                        <div className="flex gap-1">
                          <div className="w-3 h-3 border border-white/60 rounded-sm" />
                        </div>
                      </div>
                      {/* Content */}
                      <div className="p-4 mt-4">
                        <div className="w-10 h-10 bg-white/20 rounded-xl mb-3 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">A</span>
                        </div>
                        <div className="w-24 h-2 bg-white/30 rounded mb-2" />
                        <div className="w-16 h-2 bg-white/20 rounded mb-6" />
                        <div className="space-y-2">
                          <div className="w-full h-8 bg-white/10 rounded-lg" />
                          <div className="w-full h-8 bg-white/10 rounded-lg" />
                          <div className="w-full h-8 bg-white/10 rounded-lg" />
                        </div>
                      </div>
                      {/* Google Play Badge */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                        <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur rounded-full px-3 py-1.5">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
                            <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                          </svg>
                          <span className="text-white text-[10px] font-medium">Google Play</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* iPhone */}
                <div className="relative z-20 transform rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="w-48 h-96 bg-slate-900 rounded-[2.5rem] p-2 shadow-2xl shadow-slate-900/50">
                    {/* Notch */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-30" />
                    <div className="w-full h-full bg-gradient-to-br from-violet-600 to-indigo-600 rounded-[2rem] relative overflow-hidden">
                      {/* Content */}
                      <div className="p-5 mt-10">
                        <div className="w-12 h-12 bg-white/20 rounded-2xl mb-4 flex items-center justify-center">
                          <span className="text-white font-bold">A</span>
                        </div>
                        <div className="w-28 h-3 bg-white/30 rounded mb-2" />
                        <div className="w-20 h-2 bg-white/20 rounded mb-8" />
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <div className="h-16 bg-white/10 rounded-xl" />
                          <div className="h-16 bg-white/10 rounded-xl" />
                        </div>
                        <div className="space-y-2">
                          <div className="w-full h-10 bg-white/10 rounded-xl" />
                          <div className="w-full h-10 bg-white/10 rounded-xl" />
                        </div>
                      </div>
                      {/* App Store Badge */}
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                        <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur rounded-full px-3 py-1.5">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          <span className="text-white text-[10px] font-medium">App Store</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
